import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { CampaignPerformancePanel } from '@/components/performance/CampaignPerformancePanel';

type CampaignPerformanceDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  campaignId: string;
  campaignName: string;
};

export function CampaignPerformanceDialog({
  isOpen,
  onClose,
  profileId,
  campaignId,
  campaignName,
}: CampaignPerformanceDialogProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-transparent px-4 pb-4 pt-44 sm:px-6 sm:pb-6 sm:pt-48"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-performance-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-3 top-3 z-20 rounded-full p-1.5 text-foreground-muted hover:bg-surface-muted hover:text-foreground sm:right-4 sm:top-4"
          onClick={onClose}
          aria-label="Close campaign performance graph"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <div className="overflow-y-auto p-4 sm:p-6">
          <CampaignPerformancePanel
            key={campaignId}
            profileId={profileId}
            campaignId={campaignId}
            campaignName={campaignName}
            layout="dialog"
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
